'use client';

import type { SVGProps } from 'react';
import { useId } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { readonly size?: number; readonly active?: boolean };

function base(size: number | undefined, props: SVGProps<SVGSVGElement>) {
  const s = size ?? 24;
  return {
    width: s,
    height: s,
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
    ...props,
  };
}

const OUTLINE = {
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  fill: 'none',
};

// Active is a filled silhouette with its detail knocked through in the surface colour: the door of the house,
// the lines on the receipt. The silhouette itself carries no stroke — an outline in the surface colour read as
// a white halo on the tinted pill behind it.
const SOLID = { fill: 'currentColor', stroke: 'none' };

const DETAIL = {
  stroke: 'var(--primary-foreground)',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  fill: 'none',
};

// A line-drawn glyph has no interior to fill; it thickens instead.
const SOLID_LINE = {
  stroke: 'currentColor',
  strokeWidth: 2.25,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  fill: 'none',
};

// Detail strokes that meet the silhouette's edge are clipped to it, or their round caps poke out as specks.
function clipId(id: string): string {
  return `nav-icon-${id.replace(/[^a-zA-Z0-9_-]/g, '')}`;
}

const HOUSE_BODY =
  'M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z';
const HOUSE_DOOR = 'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8';

export function NavHouse({ size, active, ...props }: IconProps) {
  const clip = clipId(useId());
  return (
    <svg {...base(size, props)}>
      {active ? (
        <>
          <defs>
            <clipPath id={clip}>
              <path d={HOUSE_BODY} />
            </clipPath>
          </defs>
          <path {...SOLID} d={HOUSE_BODY} />
          <path {...DETAIL} d={HOUSE_DOOR} clipPath={`url(#${clip})`} />
        </>
      ) : (
        <>
          <path {...OUTLINE} d={HOUSE_BODY} />
          <path {...OUTLINE} d={HOUSE_DOOR} />
        </>
      )}
    </svg>
  );
}

export function NavUsers({ size, active, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      {active ? (
        <>
          <circle {...SOLID} cx={9} cy={7} r={4} />
          <path {...SOLID} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2Z" />
          {/* The second person is the part of them not hidden behind the first: a full-depth crescent of head and
              a shoulder of the same weight, clear of the first by a stroke's width so they read as two. */}
          <path {...SOLID} d="M16 3.128a4 4 0 0 1 0 7.744Z" />
          <path {...SOLID} d="M17.6 15.2a4.4 4.4 0 0 1 4.4 4.3V21h-3.4v-2a6 6 0 0 0-1-3.8Z" />
        </>
      ) : (
        <>
          <path {...OUTLINE} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <path {...OUTLINE} d="M16 3.128a4 4 0 0 1 0 7.744" />
          <path {...OUTLINE} d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <circle {...OUTLINE} cx={9} cy={7} r={4} />
        </>
      )}
    </svg>
  );
}

export function NavListChecks({ size, active, ...props }: IconProps) {
  const s = active ? SOLID_LINE : OUTLINE;
  return (
    <svg {...base(size, props)}>
      <path {...s} d="m3 17 2 2 4-4" />
      <path {...s} d="m3 7 2 2 4-4" />
      <path {...s} d="M13 6h8" />
      <path {...s} d="M13 12h8" />
      <path {...s} d="M13 18h8" />
    </svg>
  );
}

export function NavTrendingUp({ size, active, ...props }: IconProps) {
  const s = active ? SOLID_LINE : OUTLINE;
  return (
    <svg {...base(size, props)}>
      <path {...s} d="M16 7h6v6" />
      <path {...s} d="m22 7-8.5 8.5-5-5L2 17" />
    </svg>
  );
}

export function NavLandmark({ size, active, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      {active ? (
        <>
          <path {...SOLID} d="M12 2.4 2.6 9.7A1 1 0 0 0 3.2 11.5h17.6a1 1 0 0 0 .6-1.8Z" />
          <path {...SOLID} d="M5 13h2.2v6H5Zm5.9 0h2.2v6h-2.2Zm5.9 0H19v6h-2.2Z" />
          <path {...SOLID} d="M2.8 20.6h18.4a1 1 0 0 1 0 2H2.8a1 1 0 0 1 0-2Z" />
        </>
      ) : (
        <>
          <path
            {...OUTLINE}
            d="M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z"
          />
          <path {...OUTLINE} d="M6 18v-7" />
          <path {...OUTLINE} d="M10 18v-7" />
          <path {...OUTLINE} d="M14 18v-7" />
          <path {...OUTLINE} d="M18 18v-7" />
          <path {...OUTLINE} d="M3 22h18" />
        </>
      )}
    </svg>
  );
}

const RECEIPT_BODY = 'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z';

export function NavReceipt({ size, active, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      {active ? (
        <>
          <path {...SOLID} d={RECEIPT_BODY} />
          <path {...DETAIL} strokeWidth={1.5} d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
          <path {...DETAIL} strokeWidth={1.5} d="M12 17.5v-11" />
        </>
      ) : (
        <>
          <path {...OUTLINE} d={RECEIPT_BODY} />
          <path {...OUTLINE} d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
          <path {...OUTLINE} d="M12 17.5v-11" />
        </>
      )}
    </svg>
  );
}

const COMPASS_NEEDLE = 'm16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z';

export function NavCompass({ size, active, ...props }: IconProps) {
  return (
    <svg {...base(size, props)}>
      <circle {...(active ? SOLID : OUTLINE)} cx={12} cy={12} r={10} />
      <path {...(active ? DETAIL : OUTLINE)} d={COMPASS_NEEDLE} />
    </svg>
  );
}

const FILE_BODY = 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z';

export function NavFileText({ size, active, ...props }: IconProps) {
  const clip = clipId(useId());
  return (
    <svg {...base(size, props)}>
      {active ? (
        <>
          <defs>
            <clipPath id={clip}>
              <path d={FILE_BODY} />
            </clipPath>
          </defs>
          <path {...SOLID} d={FILE_BODY} />
          <g {...DETAIL} clipPath={`url(#${clip})`}>
            <path d="M14 2v4a2 2 0 0 0 2 2h4" />
            <path d="M10 9H8" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
          </g>
        </>
      ) : (
        <>
          <path {...OUTLINE} d={FILE_BODY} />
          <path {...OUTLINE} d="M14 2v4a2 2 0 0 0 2 2h4" />
          <path {...OUTLINE} d="M10 9H8" />
          <path {...OUTLINE} d="M16 13H8" />
          <path {...OUTLINE} d="M16 17H8" />
        </>
      )}
    </svg>
  );
}

const MESSAGE_CIRCLE_BODY = 'M7.9 20A9 9 0 1 0 4 16.1L2 22Z';
const QUESTION_MARK_HOOK = 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3';
const QUESTION_MARK_DOT = 'M12 17h.01';

export function NavMessageCircleQuestion({ size, active, ...props }: IconProps) {
  const clip = clipId(useId());
  return (
    <svg {...base(size, props)}>
      {active ? (
        <>
          <defs>
            <clipPath id={clip}>
              <path d={MESSAGE_CIRCLE_BODY} />
            </clipPath>
          </defs>
          <path {...SOLID} d={MESSAGE_CIRCLE_BODY} />
          <g {...DETAIL} clipPath={`url(#${clip})`}>
            <path d={QUESTION_MARK_HOOK} />
            <path d={QUESTION_MARK_DOT} />
          </g>
        </>
      ) : (
        <>
          <path {...OUTLINE} d={MESSAGE_CIRCLE_BODY} />
          <path {...OUTLINE} d={QUESTION_MARK_HOOK} />
          <path {...OUTLINE} d={QUESTION_MARK_DOT} />
        </>
      )}
    </svg>
  );
}
